import React from 'react';

function fmt(v, suffix = '') {
  if (v == null || v === '') return '—';
  return `${v}${suffix}`;
}

function fmtNum(v, digits = 2, suffix = '') {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(digits)}${suffix}`;
}

function ProfileSection({ title, children, footnote }) {
  if (!children) return null;
  return (
    <section className="profile-section">
      <div className="profile-section-title">{title}</div>
      {children}
      {footnote ? <p className="decision-muted profile-section-foot">{footnote}</p> : null}
    </section>
  );
}

function ProfileKv({ label, value, wide, hint, sourceLabel }) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div className={`profile-kv${wide ? ' profile-kv-wide' : ''}`}>
      <div className="profile-kv-label">
        {label}
        {sourceLabel ? <span className="profile-kv-source">{sourceLabel}</span> : null}
      </div>
      <div className="profile-kv-value">{value}</div>
      {hint ? <div className="profile-kv-hint">{hint}</div> : null}
    </div>
  );
}

function ProfileTags({ tags }) {
  if (!tags?.length) return null;
  return (
    <div className="profile-tags">
      {tags.map((tag) => (
        <span className={`profile-tag tone-${tag.tone || 'neutral'}`} key={tag.key}>
          {tag.label}
        </span>
      ))}
    </div>
  );
}

export function ProductFundamentalsCard({ profile }) {
  if (!profile) return null;

  const ageText =
    profile.age_days != null
      ? `${profile.establish_date}（运作 ${profile.age_days} 天）`
      : profile.establish_date || null;

  const constraintFields = profile.constraint_fields || [];

  return (
    <div className="profile-card profile-fundamentals">
      <ProfileTags tags={profile.tags} />

      <ProfileSection title="产品核心信息">
        <div className="profile-grid profile-grid-compact">
          <ProfileKv wide label="产品全称" value={fmt(profile.product_full_name || profile.product_name)} />
          <ProfileKv label="产品编号" value={fmt(profile.product_id)} />
          <ProfileKv label="销售代码" value={fmt(profile.sale_code)} />
          <ProfileKv label="登记编码" value={fmt(profile.product_code)} />
          <ProfileKv label="代销机构" value={fmt(profile.agent_org)} />
          <ProfileKv label="管理人" value={fmt(profile.manager_org)} />
          <ProfileKv label="产品类型" value={fmt(profile.product_type)} />
          <ProfileKv label="产品系列" value={fmt(profile.product_series || profile.product_category)} />
          {profile.product_series && profile.product_category && profile.product_series !== profile.product_category ? (
            <ProfileKv label="产品分类" value={fmt(profile.product_category)} />
          ) : null}
          <ProfileKv label="风险等级" value={fmt(profile.risk_level)} />
          <ProfileKv
            label="存续状态"
            value={fmt(profile.status_label || profile.status)}
            hint={profile.status_label && profile.status !== profile.status_label ? `码值 ${profile.status}` : null}
          />
          <ProfileKv label="成立日" value={ageText} />
          <ProfileKv label="上市日" value={fmt(profile.market_date)} />
          <ProfileKv label="起购金额" value={fmt(profile.min_purchase)} />
        </div>
      </ProfileSection>

      <ProfileSection title="业绩目标" footnote={profile.constraints_note}>
        <div className="profile-grid profile-grid-compact">
          {constraintFields.map((field) => (
            <ProfileKv
              key={field.key}
              label={field.label}
              value={fmt(field.value)}
              wide={field.wide}
              hint={field.hint}
              sourceLabel={field.source_label}
            />
          ))}
        </div>
      </ProfileSection>

      {profile.last_update_time ? (
        <p className="decision-muted profile-meta-foot">静态信息更新 {profile.last_update_time}</p>
      ) : null}
    </div>
  );
}

export function ProductNavPerformanceCard({ profile }) {
  if (!profile) return null;

  return (
    <div className="profile-card">
      <div className="profile-grid profile-grid-compact">
        <ProfileKv label="净值日期" value={fmt(profile.latest_nav_date)} />
        <ProfileKv label="单位净值" value={fmtNum(profile.latest_unit_nav, 6)} />
        <ProfileKv label="万份收益" value={fmtNum(profile.latest_daily_income, 2)} />
        <ProfileKv label="7日万收" value={fmtNum(profile.avg_7d_income, 2)} />
        <ProfileKv label="14日万收" value={fmtNum(profile.avg_14d_income, 2)} />
        <ProfileKv label="7日年化" value={fmtNum(profile.annual_7d_yield, 2, '%')} />
        <ProfileKv label="14日年化" value={fmtNum(profile.annual_14d_yield, 2, '%')} />
        <ProfileKv label="1月年化" value={fmtNum(profile.annual_1m_yield, 2, '%')} />
        <ProfileKv wide label="成立以来年化" value={fmtNum(profile.annual_since_yield, 2, '%')} />
      </div>
    </div>
  );
}

export default function ProductProfileCard({ profile }) {
  return (
    <>
      <ProductFundamentalsCard profile={profile} />
      <ProductNavPerformanceCard profile={profile} />
    </>
  );
}
